import { useI18n } from "@excalidraw/excalidraw/i18n";
import { WelcomeScreen } from "@excalidraw/excalidraw/index";
import React, { useRef } from "react";

import { LoadIcon, UploadIcon } from "@excalidraw/excalidraw/components/icons";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";
import { CaptureUpdateAction } from "@excalidraw/excalidraw/index";

import { useSetAtom } from "../app-jotai";
import { customFileLibraryDialogAtom } from "../app-jotai";
import { CustomFileStorage } from "../data/customFileStorage";

export const AppWelcomeScreen: React.FC<{
  onCollabDialogOpen: () => any;
  isCollabEnabled: boolean;
  excalidrawAPI?: any;
}> = React.memo(({ excalidrawAPI }) => {
  const { t } = useI18n();
  const setCustomLibraryDialog = useSetAtom(customFileLibraryDialogAtom);
  const setAppState = useExcalidrawSetAppState();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headingContent = t("welcomeScreen.app.center_heading");

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      // Load the file data
      const data = await loadFromBlob(file, null, null);

      // Clear current file tracking to create a new file
      CustomFileStorage.clearCurrentFile();

      // Update the scene with the loaded data using the excalidraw API
      if (excalidrawAPI) {
        excalidrawAPI.updateScene({
          elements: data.elements,
          appState: data.appState,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Add binary files if any
        if (data.files && Object.keys(data.files).length > 0) {
          excalidrawAPI.addFiles(Object.values(data.files));
        }
      }
    } catch (error) {
      console.error("Failed to load file:", error);
      setAppState({
        errorMessage: "Failed to load file. Please try again.",
      });
    }

    // Reset the input so the same file can be uploaded again
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleMyFilesClick = () => {
    setCustomLibraryDialog({ isOpen: true });
  };

  return (
    <WelcomeScreen>
      <WelcomeScreen.Hints.MenuHint>
        {t("welcomeScreen.app.menuHint")}
      </WelcomeScreen.Hints.MenuHint>
      <WelcomeScreen.Hints.ToolbarHint />
      <WelcomeScreen.Hints.HelpHint />
      <WelcomeScreen.Center>
        <WelcomeScreen.Center.Logo>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <img
              src="/mjolnir.png"
              alt="Mjölnote"
              style={{ width: "80px", height: "80px", objectFit: "contain" }}
            />
            <div
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                color: "var(--color-primary)",
              }}
            >
              Mjölnote
            </div>
          </div>
        </WelcomeScreen.Center.Logo>
        <WelcomeScreen.Center.Heading>
          {headingContent}
        </WelcomeScreen.Center.Heading>
        <WelcomeScreen.Center.Menu>
          <WelcomeScreen.Center.MenuItem
            onSelect={handleUploadClick}
            icon={UploadIcon}
            shortcut={null}
          >
            Upload
          </WelcomeScreen.Center.MenuItem>
          <WelcomeScreen.Center.MenuItem
            onSelect={handleMyFilesClick}
            icon={LoadIcon}
            shortcut={null}
          >
            My Files
          </WelcomeScreen.Center.MenuItem>
        </WelcomeScreen.Center.Menu>
        <input
          ref={fileInputRef}
          type="file"
          accept=".excalidraw,.excalidrawlib"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </WelcomeScreen.Center>
    </WelcomeScreen>
  );
});
